import type { TaskView } from '@/lib/api/generated/model';
import type { BoardStepSection } from './boardModel';
import { hasLiveExecution, latestExecution } from './executionStatus';

/**
 * The one thing to do next with a task (spec 125 FR-001, research R2).
 *
 * Derived, never stored, and in the same order `deriveCardExecutionState`
 * reads a task, so the verb in the drawer header never contradicts the state
 * chip printed beside it:
 *
 * 1. a run in flight — queued included, which the board already keeps Stop
 *    offered for — is stopped;
 * 2. a task on the start step is started, whatever happened to it before: a
 *    task sent back to Backlog is started again, not re-run, because re-running
 *    repeats the latest execution on *its* step, which is not where the task is;
 * 3. a task on a human gate, or on a step that declares more than one way on,
 *    waits on a person's decision, and the declared ways on are the outcomes;
 * 4. a task whose latest run failed, stopped or was blocked is run again;
 * 5. otherwise there is nothing to press, and the details tab's "Next step"
 *    section still lists whatever the step declares.
 */
export interface StepRef {
  stepId: string;
  label: string;
}

export interface Outcome {
  /** The transition's key. */
  key: string;
  /** Authored label, else the target step's label, else the key. */
  label: string;
  /** The step the outcome moves the task to. */
  to: string;
}

/**
 * What a person chose on a row, reported instead of done (spec 154 R2): the
 * Overview holds it for 6 seconds before anything is sent.
 */
export type TaskChoice =
  | { kind: 'decide'; outcome: Outcome; targetLabel: string }
  | { kind: 'run-again' }
  | { kind: 'start-subtasks' };

export type PrimaryAction =
  | { kind: 'start'; target: StepRef; others: StepRef[] }
  | { kind: 'decide'; outcomes: Outcome[] }
  | { kind: 'stop' }
  | { kind: 'run-again' }
  | { kind: 'none' };

const RERUNNABLE = new Set(['failed', 'stopped', 'blocked']);

/**
 * The declared ways on from `step`, in declaration order, one per target.
 *
 * Deduplicated by target because two transitions to one step are one button
 * as far as the person is concerned — the move is addressed by step, not by
 * transition (the service's move takes a target step id).
 */
export function outcomesFor(
  step: BoardStepSection | undefined,
  steps: readonly BoardStepSection[],
): Outcome[] {
  const byId = new Map(steps.map((s) => [s.stepId, s]));
  const seen = new Set<string>();
  const outcomes: Outcome[] = [];
  for (const transition of step?.transitions ?? []) {
    if (seen.has(transition.to)) continue;
    seen.add(transition.to);
    outcomes.push({
      key: transition.key,
      label:
        transition.label ?? byId.get(transition.to)?.label ?? transition.key,
      to: transition.to,
    });
  }
  return outcomes;
}

/**
 * Where Start can send a task: every step of its workflow except the one it
 * stands on and the terminal Done column, in board order (research R1). The
 * service's move is free placement, so this is the whole workflow, not only
 * what some transition reaches — Backlog declares no transitions at all.
 */
function startTargets(
  currentStepId: string,
  steps: readonly BoardStepSection[],
): StepRef[] {
  // By id, not only by role: a workflow whose phases also list the system
  // steps would otherwise offer Backlog as somewhere to start a Backlog task.
  const systemIds = new Set(
    steps.filter((step) => step.systemRole).map((step) => step.stepId),
  );
  const seen = new Set<string>();
  return steps
    .filter((step) => {
      if (step.stepId === currentStepId || systemIds.has(step.stepId)) {
        return false;
      }
      if (seen.has(step.stepId)) return false;
      seen.add(step.stepId);
      return true;
    })
    .map((step) => ({ stepId: step.stepId, label: step.label }));
}

export function derivePrimaryAction(
  task: TaskView,
  currentStep: BoardStepSection | undefined,
  steps: readonly BoardStepSection[],
): PrimaryAction {
  if (hasLiveExecution(task)) return { kind: 'stop' };

  if (currentStep?.systemRole === 'start') {
    const [target, ...others] = startTargets(currentStep.stepId, steps);
    return target ? { kind: 'start', target, others } : { kind: 'none' };
  }

  if (
    currentStep &&
    (currentStep.kind === 'human_gate' ||
      (currentStep.transitions?.length ?? 0) > 1)
  ) {
    const outcomes = outcomesFor(currentStep, steps);
    if (outcomes.length > 0) return { kind: 'decide', outcomes };
  }

  const latest = latestExecution(task.executions ?? []);
  if (latest && RERUNNABLE.has(latest.status)) return { kind: 'run-again' };

  return { kind: 'none' };
}

/** The words a control for this action carries (header density). */
export function primaryActionLabel(action: PrimaryAction): string | null {
  switch (action.kind) {
    case 'start':
      return `Start: ${action.target.label}`;
    case 'decide':
      return action.outcomes[0]?.label ?? null;
    case 'stop':
      return 'Stop';
    case 'run-again':
      return 'Run again';
    case 'none':
      return null;
  }
}
